"use client";
import type { IndustryAccent } from "@/components/marketing";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import { industryAccent } from "../../industryPage.util";
import { useGoalProgress } from "./hooks";

export interface GoalProgressCopy {
  ariaLabel: string;
  goal: number;
  // "sessions", "visits", "packs scanned"...
  unit: string;
  title: string;
  addLabel: string;
  resetLabel: string;
  reward: { kicker: string; title: string; note: string };
}

interface GoalProgressProps {
  copy: GoalProgressCopy;
  // "{done} of {goal} {unit}".
  countTemplate: string;
  accent: IndustryAccent;
}

// "Try a ..." - the preview's `ind_goal`: a card with a progress bar and
// an add button; at the goal the add button stops and the reward card pops
// in beside it (the card sits alone, centred, until then).
const GoalProgress = ({ copy, countTemplate, accent }: GoalProgressProps) => {
  const tone = industryAccent[accent];
  const { done, percent, isComplete, add, reset } = useGoalProgress(copy.goal);
  const count = countTemplate
    .replace("{done}", String(done))
    .replace("{goal}", String(copy.goal))
    .replace("{unit}", copy.unit);

  return (
    <figure
      role="group"
      aria-label={copy.ariaLabel}
      className={cn(
        "m-0 mx-auto grid gap-5",
        isComplete ? "max-w-[880px] md:grid-cols-2" : "max-w-[520px]",
      )}
    >
      <div className="grid content-center gap-3 rounded-[26px] bg-white p-6 shadow-card">
        <b className="text-xl leading-snug">{copy.title}</b>
        <Progress
          value={percent}
          aria-label={copy.title}
          getValueLabel={() => count}
          className="h-3.5 bg-line"
          indicatorClassName={cn(
            tone.solid,
            "rounded-full duration-300 motion-reduce:transition-none",
          )}
        />
        <p
          aria-live="polite"
          className={cn("text-[1.6rem] font-extrabold tracking-[-0.5px]", tone.ink)}
        >
          {count}
        </p>
        <div className="flex flex-wrap gap-2.5">
          <Button
            type="button"
            variant="brand"
            size="pillSm"
            onClick={add}
            disabled={isComplete}
          >
            {copy.addLabel}
          </Button>
          <Button type="button" variant="brandOutline" size="pillSm" onClick={reset}>
            {copy.resetLabel}
          </Button>
        </div>
      </div>
      {/* Kept mounted (out of the grid flow while empty) so the reward is
          announced when it appears. */}
      <div aria-live="polite" className={cn(!isComplete && "sr-only")}>
        {isComplete && (
          <div
            className={cn(
              "grid h-full content-center justify-items-center gap-3 rounded-[26px] border-2 border-dashed p-6 text-center shadow-card motion-safe:animate-in motion-safe:fade-in motion-safe:zoom-in-90 motion-safe:animation-duration-500",
              tone.soft,
              tone.border,
            )}
          >
            <span
              className={cn(
                "text-[12px] font-extrabold tracking-[0.08em] uppercase",
                tone.ink,
              )}
            >
              {copy.reward.kicker}
            </span>
            <b className="text-[1.7rem] leading-[1.15]">{copy.reward.title}</b>
            <small className="text-[14px] text-ink-muted">{copy.reward.note}</small>
          </div>
        )}
      </div>
    </figure>
  );
};

export default GoalProgress;
